import { useState, useEffect, useCallback, useRef } from "react";
import { RetrospectiveDTO } from "@retrospecter/shared/types";
import {
  apiGet,
  UnauthorizedError,
  ForbiddenError,
  NotFoundError,
} from "../api/client";

/**
 * Discriminated failure kind, so callers branch on the HTTP outcome instead of
 * substring-matching an error message.
 */
export type RetroErrorKind = "unauthorized" | "forbidden" | "not_found" | "unknown";

export interface RetroState {
  retro?: RetrospectiveDTO;
  /** True only while there is nothing to show yet — never during a refresh. */
  loading: boolean;
  /** True while re-reading a board that is already on screen. */
  refreshing: boolean;
  errorKind?: RetroErrorKind;
  errorMessage?: string;
}

function classifyError(error: unknown): RetroErrorKind {
  if (error instanceof UnauthorizedError) return "unauthorized";
  if (error instanceof ForbiddenError) return "forbidden";
  if (error instanceof NotFoundError) return "not_found";
  return "unknown";
}

export function useRetro(publicId: string) {
  const [state, setState] = useState<RetroState>({
    loading: true,
    refreshing: false,
  });

  // Refetches arrive from several places at once — a mutation finishing and the
  // socket event it triggered. Collapse them into one in-flight request, with a
  // single follow-up if anything came in while it was running.
  const inFlight = useRef(false);
  const queued = useRef(false);

  const load = useCallback(async () => {
    if (!publicId) return;

    if (inFlight.current) {
      queued.current = true;
      return;
    }
    inFlight.current = true;

    // Keep whatever is on screen; only flag that a refresh is happening.
    setState((prev) =>
      prev.retro ? { ...prev, refreshing: true } : { ...prev, loading: true }
    );

    try {
      const data: RetrospectiveDTO = await apiGet(`/api/retrospectives/${publicId}`);
      setState({ retro: data, loading: false, refreshing: false });
    } catch (error) {
      const errorKind = classifyError(error);

      setState((prev) => ({
        // 401/403/404 are real changes in what this viewer may see, so the
        // board goes away. A transient failure should not blank a working
        // board — keep showing it and let the next refresh recover.
        retro: errorKind === "unknown" ? prev.retro : undefined,
        loading: false,
        refreshing: false,
        errorKind,
        errorMessage: error instanceof Error ? error.message : String(error),
      }));
    } finally {
      inFlight.current = false;

      if (queued.current) {
        queued.current = false;
        load();
      }
    }
  }, [publicId]);

  // A different board means the one on screen is no longer relevant.
  useEffect(() => {
    setState({ loading: true, refreshing: false });
  }, [publicId]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, refetch: load };
}
